import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  BookOpenIcon,
  BugAntIcon,
  XMarkIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  SunIcon,
  CloudIcon,
  GlobeAltIcon,
  BeakerIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

// ---------------------------------------------------------------------------
// Inline Types
// ---------------------------------------------------------------------------

type CropCategory = 'All' | 'Cereals' | 'Legumes' | 'Tubers' | 'Cash Crops' | 'Vegetables' | 'Fruits';
type PestType = 'All' | 'Insects' | 'Fungi' | 'Bacteria' | 'Viruses' | 'Nematodes' | 'Weeds';
type Severity = 'low' | 'medium' | 'high' | 'critical';
type TabId = 'crops' | 'pests' | 'search';

interface GrowthStage {
  name: string;
  durationDays: number;
  description: string;
}

interface GrowingConditions {
  temperature: { min: number; max: number; unit: string };
  rainfall: { min: number; max: number; unit: string };
  altitude: { min: number; max: number; unit: string };
  soilTypes: string[];
}

interface MarketInfo {
  averagePrice: number;
  currency: string;
  unit: string;
  markets: string[];
}

interface CropItem {
  id: string;
  name: string;
  scientificName: string;
  category: string;
  description: string;
  growingConditions?: GrowingConditions;
  growthStages?: GrowthStage[];
  commonPests?: string[];
  commonDiseases?: string[];
  marketInfo?: MarketInfo;
  yieldPerHectare?: { min: number; max: number; unit: string };
}

interface ControlMethods {
  cultural: string[];
  biological: string[];
  chemical: string[];
}

interface PestItem {
  id: string;
  name: string;
  scientificName: string;
  type: string;
  severity: Severity;
  description: string;
  affectedCrops?: string[];
  symptoms?: string[];
  controlMethods?: ControlMethods;
  preventionTips?: string[];
  seasonalRisk?: number[]; // 1-12 month indices where risk is elevated
}

interface SearchResult {
  type: 'crop' | 'pest';
  id: string;
  name: string;
  scientificName: string;
  category?: string;
  pestType?: string;
  severity?: Severity;
  description: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function apiFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${API}${path}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const json = await res.json();
  return json.data ?? json;
}

const CROP_CATEGORIES: CropCategory[] = ['All', 'Cereals', 'Legumes', 'Tubers', 'Cash Crops', 'Vegetables', 'Fruits'];
const PEST_TYPES: PestType[] = ['All', 'Insects', 'Fungi', 'Bacteria', 'Viruses', 'Nematodes', 'Weeds'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CATEGORY_COLORS: Record<string, string> = {
  Cereals: 'bg-amber-100 text-amber-800',
  Legumes: 'bg-green-100 text-green-800',
  Tubers: 'bg-orange-100 text-orange-800',
  'Cash Crops': 'bg-purple-100 text-purple-800',
  Vegetables: 'bg-emerald-100 text-emerald-800',
  Fruits: 'bg-pink-100 text-pink-800',
};

const PEST_TYPE_COLORS: Record<string, string> = {
  Insects: 'bg-yellow-100 text-yellow-800',
  Fungi: 'bg-violet-100 text-violet-800',
  Bacteria: 'bg-red-100 text-red-800',
  Viruses: 'bg-rose-100 text-rose-800',
  Nematodes: 'bg-teal-100 text-teal-800',
  Weeds: 'bg-lime-100 text-lime-800',
};

const SEVERITY_CONFIG: Record<Severity, { dot: string; label: string; bg: string }> = {
  low: { dot: 'bg-green-500', label: 'Low', bg: 'bg-green-50 text-green-700 ring-green-600/20' },
  medium: { dot: 'bg-yellow-500', label: 'Medium', bg: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20' },
  high: { dot: 'bg-orange-500', label: 'High', bg: 'bg-orange-50 text-orange-700 ring-orange-600/20' },
  critical: { dot: 'bg-red-500', label: 'Critical', bg: 'bg-red-50 text-red-700 ring-red-600/20' },
};

// ---------------------------------------------------------------------------
// Skeleton Loader Components
// ---------------------------------------------------------------------------

function CardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-card p-5 animate-pulse">
      <div className="flex items-start justify-between mb-3">
        <div className="space-y-2 flex-1">
          <div className="h-5 bg-gray-200 rounded w-3/5" />
          <div className="h-3.5 bg-gray-100 rounded w-2/5" />
        </div>
        <div className="h-6 w-16 bg-gray-100 rounded-full" />
      </div>
      <div className="space-y-2 mt-4">
        <div className="h-3.5 bg-gray-100 rounded w-full" />
        <div className="h-3.5 bg-gray-100 rounded w-4/5" />
      </div>
    </div>
  );
}

function GridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function KnowledgeBase() {
  // State
  const [activeTab, setActiveTab] = useState<TabId>('crops');
  const [cropCategory, setCropCategory] = useState<CropCategory>('All');
  const [pestType, setPestType] = useState<PestType>('All');
  const [cropSearch, setCropSearch] = useState('');
  const [pestSearch, setPestSearch] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');
  const [selectedCrop, setSelectedCrop] = useState<CropItem | null>(null);
  const [selectedPest, setSelectedPest] = useState<PestItem | null>(null);

  // -------------------------------------------------------------------------
  // Data Fetching
  // -------------------------------------------------------------------------

  const cropQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (cropCategory !== 'All') params.set('category', cropCategory);
    if (cropSearch.trim()) params.set('search', cropSearch.trim());
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, [cropCategory, cropSearch]);

  const pestQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (pestType !== 'All') params.set('type', pestType);
    if (pestSearch.trim()) params.set('search', pestSearch.trim());
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, [pestType, pestSearch]);

  const {
    data: crops = [],
    isLoading: cropsLoading,
    isError: cropsError,
  } = useQuery<CropItem[]>({
    queryKey: ['knowledge-crops', cropCategory, cropSearch],
    queryFn: () => apiFetch<CropItem[]>(`/knowledge/crops${cropQueryParams}`),
  });

  const {
    data: pests = [],
    isLoading: pestsLoading,
    isError: pestsError,
  } = useQuery<PestItem[]>({
    queryKey: ['knowledge-pests', pestType, pestSearch],
    queryFn: () => apiFetch<PestItem[]>(`/knowledge/pests${pestQueryParams}`),
  });

  const {
    data: searchResults = [],
    isLoading: searchLoading,
    isError: searchError,
  } = useQuery<SearchResult[]>({
    queryKey: ['knowledge-search', globalSearch],
    queryFn: () => apiFetch<SearchResult[]>(`/knowledge/search?q=${encodeURIComponent(globalSearch.trim())}`),
    enabled: activeTab === 'search' && globalSearch.trim().length >= 2,
  });

  // Fetch full crop detail when a crop is selected
  const { data: cropDetail } = useQuery<CropItem>({
    queryKey: ['knowledge-crop-detail', selectedCrop?.name],
    queryFn: () => apiFetch<CropItem>(`/knowledge/crops/${encodeURIComponent(selectedCrop!.name)}`),
    enabled: !!selectedCrop,
  });

  // Fetch full pest detail when a pest is selected
  const { data: pestDetail } = useQuery<PestItem>({
    queryKey: ['knowledge-pest-detail', selectedPest?.name],
    queryFn: () => apiFetch<PestItem>(`/knowledge/pests/${encodeURIComponent(selectedPest!.name)}`),
    enabled: !!selectedPest,
  });

  const activeCrop = cropDetail ?? selectedCrop;
  const activePest = pestDetail ?? selectedPest;

  // -------------------------------------------------------------------------
  // Tab Config
  // -------------------------------------------------------------------------

  const tabs: { id: TabId; label: string; icon: typeof BookOpenIcon }[] = [
    { id: 'crops', label: 'Crops', icon: BookOpenIcon },
    { id: 'pests', label: 'Pests & Diseases', icon: BugAntIcon },
    { id: 'search', label: 'Search', icon: MagnifyingGlassIcon },
  ];

  // -------------------------------------------------------------------------
  // Render Helpers
  // -------------------------------------------------------------------------

  function renderFilterPills<T extends string>(
    options: T[],
    active: T,
    onChange: (v: T) => void,
    colorMap: Record<string, string>,
  ) {
    return (
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const isActive = opt === active;
          const colorClass = opt === 'All'
            ? (isActive ? 'bg-kenagri-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
            : (isActive
                ? 'bg-kenagri-600 text-white'
                : `${colorMap[opt] || 'bg-gray-100 text-gray-700'} hover:opacity-80`);
          return (
            <button
              key={opt}
              onClick={() => onChange(opt)}
              className={`
                px-3.5 py-1.5 rounded-full text-sm font-medium
                transition-all duration-200
                ${colorClass}
                ${isActive ? 'shadow-soft-sm ring-1 ring-kenagri-600/20' : ''}
              `}
            >
              {opt}
            </button>
          );
        })}
      </div>
    );
  }

  function renderSearchInput(value: string, onChange: (v: string) => void, placeholder: string) {
    return (
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="
            w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200
            bg-white text-sm text-gray-900 placeholder:text-gray-400
            focus:outline-none focus:ring-2 focus:ring-kenagri-500/40 focus:border-kenagri-400
            transition-all duration-200
          "
        />
        {value && (
          <button
            onClick={() => onChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  function renderEmptyState(title: string, description: string) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-2xl bg-kenagri-50 flex items-center justify-center mb-4">
          <BookOpenIcon className="h-8 w-8 text-kenagri-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-500 max-w-sm">{description}</p>
      </div>
    );
  }

  function renderErrorState(message: string) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
          <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Something went wrong</h3>
        <p className="text-sm text-gray-500 max-w-sm">{message}</p>
      </div>
    );
  }

  // =========================================================================
  // CROP CARD
  // =========================================================================

  function CropCard({ crop }: { crop: CropItem }) {
    const isExpanded = activeCrop?.id === crop.id;

    return (
      <div
        className={`
          bg-white rounded-xl border shadow-sm
          transition-all duration-300 cursor-pointer
          ${isExpanded
            ? 'border-kenagri-300 shadow-md ring-1 ring-kenagri-200 col-span-1 md:col-span-2 lg:col-span-3'
            : 'border-gray-100 hover:shadow-md hover:border-kenagri-100 hover:-translate-y-0.5'
          }
        `}
        onClick={() => setSelectedCrop(isExpanded ? null : crop)}
      >
        {/* Card Header */}
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-semibold text-gray-900 truncate">{crop.name}</h3>
                <ChevronRightIcon
                  className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                />
              </div>
              <p className="text-sm text-gray-500 italic truncate">{crop.scientificName}</p>
            </div>
            <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium ${CATEGORY_COLORS[crop.category] || 'bg-gray-100 text-gray-700'}`}>
              {crop.category}
            </span>
          </div>
          <p className="mt-3 text-sm text-gray-600 line-clamp-2">{crop.description}</p>
        </div>

        {/* Expanded Detail */}
        {isExpanded && activeCrop && (
          <div className="border-t border-kenagri-100 bg-kenagri-50/30 p-5 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Growing Conditions */}
              {activeCrop.growingConditions && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <SunIcon className="h-4 w-4 text-kenagri-600" />
                    Growing Conditions
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                      <span className="text-xs text-gray-500">Temperature</span>
                      <span className="text-sm font-medium text-gray-900">
                        {activeCrop.growingConditions.temperature.min}-{activeCrop.growingConditions.temperature.max}{activeCrop.growingConditions.temperature.unit}
                      </span>
                    </div>
                    <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                      <span className="text-xs text-gray-500">Rainfall</span>
                      <span className="text-sm font-medium text-gray-900">
                        {activeCrop.growingConditions.rainfall.min}-{activeCrop.growingConditions.rainfall.max} {activeCrop.growingConditions.rainfall.unit}
                      </span>
                    </div>
                    <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                      <span className="text-xs text-gray-500">Altitude</span>
                      <span className="text-sm font-medium text-gray-900">
                        {activeCrop.growingConditions.altitude.min}-{activeCrop.growingConditions.altitude.max} {activeCrop.growingConditions.altitude.unit}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                      <span className="text-xs text-gray-500 block mb-1">Soil Types</span>
                      <div className="flex flex-wrap gap-1">
                        {activeCrop.growingConditions.soilTypes.map((soil) => (
                          <span key={soil} className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium">
                            {soil}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Growth Stages Timeline */}
              {activeCrop.growthStages && activeCrop.growthStages.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <ArrowPathIcon className="h-4 w-4 text-kenagri-600" />
                    Growth Stages
                  </h4>
                  <div className="relative space-y-0">
                    {activeCrop.growthStages.map((stage, idx) => (
                      <div key={stage.name} className="flex gap-3">
                        {/* Timeline line + dot */}
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full border-2 flex-shrink-0 ${
                            idx === 0 ? 'border-kenagri-600 bg-kenagri-600' : 'border-kenagri-300 bg-white'
                          }`} />
                          {idx < activeCrop.growthStages!.length - 1 && (
                            <div className="w-0.5 flex-1 bg-kenagri-200 min-h-[24px]" />
                          )}
                        </div>
                        <div className="pb-4">
                          <p className="text-sm font-medium text-gray-900">{stage.name}</p>
                          <p className="text-xs text-kenagri-600 font-medium">{stage.durationDays} days</p>
                          <p className="text-xs text-gray-500 mt-0.5">{stage.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Right column: Pests, Market, Yield */}
              <div className="space-y-4">
                {/* Common Pests & Diseases */}
                {(activeCrop.commonPests?.length || activeCrop.commonDiseases?.length) && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <BugAntIcon className="h-4 w-4 text-kenagri-600" />
                      Common Pests & Diseases
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {activeCrop.commonPests?.map((pest) => (
                        <span
                          key={pest}
                          className="px-2 py-1 bg-red-50 text-red-700 rounded-lg text-xs font-medium cursor-pointer hover:bg-red-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTab('pests');
                            setPestSearch(pest);
                          }}
                        >
                          {pest}
                        </span>
                      ))}
                      {activeCrop.commonDiseases?.map((disease) => (
                        <span
                          key={disease}
                          className="px-2 py-1 bg-orange-50 text-orange-700 rounded-lg text-xs font-medium cursor-pointer hover:bg-orange-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTab('pests');
                            setPestSearch(disease);
                          }}
                        >
                          {disease}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Market Info */}
                {activeCrop.marketInfo && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <CurrencyDollarIcon className="h-4 w-4 text-kenagri-600" />
                      Market Information
                    </h4>
                    <div className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500">Avg. Price</span>
                        <span className="text-sm font-semibold text-kenagri-700">
                          {activeCrop.marketInfo.currency} {activeCrop.marketInfo.averagePrice}/{activeCrop.marketInfo.unit}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {activeCrop.marketInfo.markets.map((m) => (
                          <span key={m} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                            {m}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Yield */}
                {activeCrop.yieldPerHectare && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <ChartBarIcon className="h-4 w-4 text-kenagri-600" />
                      Yield Per Hectare
                    </h4>
                    <div className="bg-white rounded-lg px-3 py-3 border border-gray-100 flex items-center gap-3">
                      <div className="flex-1">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-kenagri-400 to-kenagri-600 rounded-full"
                            style={{ width: `${Math.min((activeCrop.yieldPerHectare.max / (activeCrop.yieldPerHectare.max * 1.3)) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                        {activeCrop.yieldPerHectare.min} - {activeCrop.yieldPerHectare.max} {activeCrop.yieldPerHectare.unit}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // =========================================================================
  // PEST CARD
  // =========================================================================

  function PestCard({ pest }: { pest: PestItem }) {
    const isExpanded = activePest?.id === pest.id;
    const sev = SEVERITY_CONFIG[pest.severity] || SEVERITY_CONFIG.low;

    return (
      <div
        className={`
          bg-white rounded-xl border shadow-sm
          transition-all duration-300 cursor-pointer
          ${isExpanded
            ? 'border-kenagri-300 shadow-md ring-1 ring-kenagri-200 col-span-1 md:col-span-2 lg:col-span-3'
            : 'border-gray-100 hover:shadow-md hover:border-kenagri-100 hover:-translate-y-0.5'
          }
        `}
        onClick={() => setSelectedPest(isExpanded ? null : pest)}
      >
        {/* Card Header */}
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${sev.dot}`} />
                <h3 className="text-base font-semibold text-gray-900 truncate">{pest.name}</h3>
                <ChevronRightIcon
                  className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                />
              </div>
              <p className="text-sm text-gray-500 italic truncate">{pest.scientificName}</p>
            </div>
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${PEST_TYPE_COLORS[pest.type] || 'bg-gray-100 text-gray-700'}`}>
                {pest.type}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${sev.bg}`}>
                {sev.label}
              </span>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-600 line-clamp-2">{pest.description}</p>
        </div>

        {/* Expanded Detail */}
        {isExpanded && activePest && (
          <div className="border-t border-kenagri-100 bg-kenagri-50/30 p-5 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Affected Crops & Symptoms */}
              <div className="space-y-4">
                {activePest.affectedCrops && activePest.affectedCrops.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-2">
                      <GlobeAltIcon className="h-4 w-4 text-kenagri-600" />
                      Affected Crops
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {activePest.affectedCrops.map((crop) => (
                        <span
                          key={crop}
                          className="px-2 py-1 bg-green-50 text-green-700 rounded-lg text-xs font-medium cursor-pointer hover:bg-green-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTab('crops');
                            setCropSearch(crop);
                          }}
                        >
                          {crop}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {activePest.symptoms && activePest.symptoms.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-2">
                      <ExclamationTriangleIcon className="h-4 w-4 text-orange-500" />
                      Symptoms
                    </h4>
                    <ul className="space-y-1.5">
                      {activePest.symptoms.map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-1.5 flex-shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Control Methods */}
              {activePest.controlMethods && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <ShieldCheckIcon className="h-4 w-4 text-kenagri-600" />
                    Control Methods
                  </h4>

                  {activePest.controlMethods.cultural.length > 0 && (
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide mb-1.5">Cultural</p>
                      <ul className="space-y-1">
                        {activePest.controlMethods.cultural.map((m, i) => (
                          <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                            <span className="text-orange-400 mt-0.5">-</span> {m}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {activePest.controlMethods.biological.length > 0 && (
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1.5">Biological</p>
                      <ul className="space-y-1">
                        {activePest.controlMethods.biological.map((m, i) => (
                          <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                            <span className="text-green-400 mt-0.5">-</span> {m}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {activePest.controlMethods.chemical.length > 0 && (
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-1.5">Chemical</p>
                      <ul className="space-y-1">
                        {activePest.controlMethods.chemical.map((m, i) => (
                          <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                            <span className="text-purple-400 mt-0.5">-</span> {m}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Prevention & Seasonal Risk */}
              <div className="space-y-4">
                {activePest.preventionTips && activePest.preventionTips.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-2">
                      <BeakerIcon className="h-4 w-4 text-kenagri-600" />
                      Prevention Tips
                    </h4>
                    <ul className="space-y-1.5">
                      {activePest.preventionTips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-kenagri-400 mt-1.5 flex-shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Seasonal Risk Calendar */}
                {activePest.seasonalRisk && activePest.seasonalRisk.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-2">
                      <CloudIcon className="h-4 w-4 text-kenagri-600" />
                      Seasonal Risk
                    </h4>
                    <div className="grid grid-cols-6 gap-1">
                      {MONTH_LABELS.map((month, idx) => {
                        const isRisk = activePest.seasonalRisk!.includes(idx + 1);
                        return (
                          <div
                            key={month}
                            className={`
                              text-center py-1.5 rounded-lg text-xs font-medium transition-colors
                              ${isRisk
                                ? 'bg-red-100 text-red-700 ring-1 ring-inset ring-red-200'
                                : 'bg-gray-50 text-gray-400'
                              }
                            `}
                          >
                            {month}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5">Highlighted months indicate elevated risk periods</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // =========================================================================
  // SEARCH RESULT CARD
  // =========================================================================

  function SearchResultCard({ result }: { result: SearchResult }) {
    const isCrop = result.type === 'crop';
    return (
      <div
        className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md hover:border-kenagri-100 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
        onClick={() => {
          if (isCrop) {
            setActiveTab('crops');
            setCropSearch(result.name);
          } else {
            setActiveTab('pests');
            setPestSearch(result.name);
          }
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`
                inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                ${isCrop ? 'bg-kenagri-100 text-kenagri-700' : 'bg-red-100 text-red-700'}
              `}>
                {isCrop ? <BookOpenIcon className="h-3 w-3" /> : <BugAntIcon className="h-3 w-3" />}
                {isCrop ? 'Crop' : 'Pest/Disease'}
              </span>
            </div>
            <h3 className="text-base font-semibold text-gray-900 truncate">{result.name}</h3>
            <p className="text-sm text-gray-500 italic truncate">{result.scientificName}</p>
          </div>
          {result.category && (
            <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium ${CATEGORY_COLORS[result.category] || 'bg-gray-100 text-gray-700'}`}>
              {result.category}
            </span>
          )}
          {result.severity && (
            <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${SEVERITY_CONFIG[result.severity]?.bg || ''}`}>
              {SEVERITY_CONFIG[result.severity]?.label}
            </span>
          )}
        </div>
        <p className="mt-2 text-sm text-gray-600 line-clamp-2">{result.description}</p>
        <div className="mt-3 flex items-center text-xs text-kenagri-600 font-medium">
          <span>View details</span>
          <ChevronRightIcon className="h-3.5 w-3.5 ml-1" />
        </div>
      </div>
    );
  }

  // =========================================================================
  // MAIN RENDER
  // =========================================================================

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Knowledge Base</h1>
        <p className="text-gray-500 mt-1">
          Crop encyclopedia and pest &amp; disease reference for Kenyan agriculture
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center py-4 px-1 border-b-2 font-medium text-sm
                transition-colors duration-200
                ${activeTab === tab.id
                  ? 'border-kenagri-600 text-kenagri-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <tab.icon className="h-5 w-5 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ================================================================= */}
      {/* CROPS TAB                                                         */}
      {/* ================================================================= */}
      {activeTab === 'crops' && (
        <div className="space-y-5">
          {/* Filters & Search */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              {renderFilterPills(CROP_CATEGORIES, cropCategory, setCropCategory, CATEGORY_COLORS)}
            </div>
            <div className="w-full sm:w-72">
              {renderSearchInput(cropSearch, setCropSearch, 'Search crops...')}
            </div>
          </div>

          {/* Grid */}
          {cropsLoading ? (
            <GridSkeleton />
          ) : cropsError ? (
            renderErrorState('Failed to load crops. Please try again later.')
          ) : crops.length === 0 ? (
            renderEmptyState(
              'No crops found',
              cropSearch || cropCategory !== 'All'
                ? 'Try adjusting your search or filters.'
                : 'No crop data is available yet. Check back later.',
            )
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {crops.map((crop) => (
                <CropCard key={crop.id} crop={crop} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ================================================================= */}
      {/* PESTS & DISEASES TAB                                              */}
      {/* ================================================================= */}
      {activeTab === 'pests' && (
        <div className="space-y-5">
          {/* Filters & Search */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              {renderFilterPills(PEST_TYPES, pestType, setPestType, PEST_TYPE_COLORS)}
            </div>
            <div className="w-full sm:w-72">
              {renderSearchInput(pestSearch, setPestSearch, 'Search pests & diseases...')}
            </div>
          </div>

          {/* Grid */}
          {pestsLoading ? (
            <GridSkeleton />
          ) : pestsError ? (
            renderErrorState('Failed to load pests & diseases. Please try again later.')
          ) : pests.length === 0 ? (
            renderEmptyState(
              'No pests or diseases found',
              pestSearch || pestType !== 'All'
                ? 'Try adjusting your search or filters.'
                : 'No pest data is available yet. Check back later.',
            )
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {pests.map((pest) => (
                <PestCard key={pest.id} pest={pest} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ================================================================= */}
      {/* GLOBAL SEARCH TAB                                                 */}
      {/* ================================================================= */}
      {activeTab === 'search' && (
        <div className="space-y-5">
          {/* Search Input */}
          <div className="max-w-2xl">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search across all crops and pests
            </label>
            {renderSearchInput(globalSearch, setGlobalSearch, 'Type at least 2 characters to search...')}
          </div>

          {/* Results */}
          {globalSearch.trim().length < 2 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-kenagri-50 flex items-center justify-center mb-4">
                <MagnifyingGlassIcon className="h-8 w-8 text-kenagri-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Search the Knowledge Base</h3>
              <p className="text-sm text-gray-500 max-w-sm">
                Enter a search term to find crops, pests, and diseases. Results will appear as you type.
              </p>
            </div>
          ) : searchLoading ? (
            <GridSkeleton />
          ) : searchError ? (
            renderErrorState('Search failed. Please try again.')
          ) : searchResults.length === 0 ? (
            renderEmptyState(
              'No results found',
              `No crops or pests match "${globalSearch}". Try a different search term.`,
            )
          ) : (
            <>
              <p className="text-sm text-gray-500">
                Found <span className="font-semibold text-gray-900">{searchResults.length}</span> result{searchResults.length !== 1 ? 's' : ''} for &ldquo;{globalSearch}&rdquo;
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                {searchResults.map((result) => (
                  <SearchResultCard key={`${result.type}-${result.id}`} result={result} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
